/* eslint-disable no-unused-vars */
import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const AuthContext = createContext({})
const API_URL = process.env.REACT_APP_API_URL

// Global callback — Navbar registers this so any component can open the auth modal
let _openAuthModal = null
export function registerAuthModalOpener(fn) { _openAuthModal = fn }
export function openAuthModal() { if (_openAuthModal) _openAuthModal() }

async function ensureUser(user, displayName = '') {
    try {
        const params = new URLSearchParams({
            user_id: user.id,
            email: user.email,
            ...(displayName && { display_name: displayName })
        })
        await fetch(`${API_URL}/api/user/ensure?${params}`, { method: 'POST' })
    } catch (e) {
        console.error('Error ensuring user:', e)
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            const u = session?.user ?? null
            setUser(u)
            if (u) ensureUser(u)
            setLoading(false)
        })

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                window.location.href = '/reset-password'
                return
            }
            // Skip SIGNED_UP event - ensureUser is called directly in signUp with displayName
            if (event === 'SIGNED_UP') return
            const u = session?.user ?? null
            // Avoid creating a new object reference for the same logged-in user —
            // Supabase fires TOKEN_REFRESHED (e.g. on tab focus) which otherwise
            // triggers refetches in any effect that depends on `user`.
            setUser(prev => (prev?.id === u?.id ? prev : u))
            if (u) ensureUser(u)
        })

        return () => subscription.unsubscribe()
    }, [])

    const signUp = async (email, password, displayName = '') => {
        const result = await supabase.auth.signUp({ email, password })
        if (result.data?.user) {
            await ensureUser(result.data.user, displayName)
        }
        return result
    }

    const signIn = async (email, password) => {
        const result = await supabase.auth.signInWithPassword({ email, password })
        if (result.data?.user) {
            await ensureUser(result.data.user)
        }
        return result
    }

    const signOut = async () => {
        return supabase.auth.signOut()
    }

    return (
        <AuthContext.Provider value={{ user, loading, signUp, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
